import React, { useState } from 'react';
import { 
  Drawer,
  Box, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemText, 
  ListItemIcon,
  IconButton,
  Divider, 
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useVendorContext } from '../context/VendorContextProvider';
import { useNavigate } from 'react-router-dom';
import { VendorProfile } from 'shared-lib';

interface VendorDrawerProps {
  open: boolean;
  onClose: () => void;
}

const VendorDrawer: React.FC<VendorDrawerProps> = ({
  open,
  onClose,
}) => {
  const { currentVendor, vendors, setCurrentVendor, archiveVendor } = useVendorContext();
  const navigate = useNavigate();
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [vendorToArchive, setVendorToArchive] = useState<string | null>(null);

  const handleVendorSelect = (vendor: VendorProfile) => {
    setCurrentVendor(vendor);
    onClose();
    navigate('/');
  };

  const handleEdit = (event: React.MouseEvent, vendorId: string) => {
    event.stopPropagation(); // Prevent vendor selection when clicking the edit button
    navigate(`/vendors/${vendorId}/edit`);
    onClose();
  };

  const handleArchiveClick = (event: React.MouseEvent, vendorId: string) => {
    event.stopPropagation(); // Prevent vendor selection when clicking the archive button
    setVendorToArchive(vendorId);
    setArchiveDialogOpen(true);
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setVendorToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    if (vendorToArchive) {
      await archiveVendor(vendorToArchive);
      setArchiveDialogOpen(false);
      setVendorToArchive(null);
    }
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { 
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            padding: '16px 0',
            '& .MuiListItemButton-root': {
              transition: 'background-color 0.3s ease',
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 171, 155, 0.1)',
                '& .MuiTypography-root': {
                  color: '#00AB9B',
                  fontWeight: 700
                },
                '& .MuiListItemIcon-root': {
                  color: '#00AB9B'
                }
              }
            },
            '& .MuiIconButton-root': {
              color: 'rgba(16, 29, 59, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(16, 29, 59, 0.04)'
              }
            },
            '& .MuiDivider-root': {
              borderColor: 'rgba(16, 29, 59, 0.2)'
            }
          }
        }}
      >
        <Box sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              color: '#101D3B',
              fontWeight: 700,
              fontSize: '18px'
            }}
          >
            Select Vendor
          </Typography>
          <List sx={{ flexGrow: 1, overflow: 'auto' }}>
            {vendors.map((vendor) => (
              <ListItem 
                key={vendor.id} 
                disablePadding
                secondaryAction={
                  <Box>
                    {/* <IconButton 
                      edge="end" 
                      onClick={(e) => handleEdit(e, vendor.id)}
                      sx={{ 
                        mr: 1,
                        color: 'rgba(16, 29, 59, 0.6)',
                        '&:hover': {
                          backgroundColor: 'rgba(16, 29, 59, 0.04)'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton> */}
                    <IconButton 
                      edge="end" 
                      onClick={(e) => handleArchiveClick(e, vendor.id)}
                      sx={{ 
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        }
                      }}
                    >
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
                sx={{
                  mb: 1,
                  '&:last-child': {
                    mb: 0
                  }
                }}
              >
                <ListItemButton
                  selected={vendor.id === currentVendor?.id}
                  onClick={() => handleVendorSelect(vendor)}
                  sx={{
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 171, 155, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 171, 155, 0.15)'
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(16, 29, 59, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EngineeringIcon 
                      fontSize="small" 
                      sx={{ 
                        color: vendor.id === currentVendor?.id ? '#00AB9B' : 'rgba(16, 29, 59, 0.6)'
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={vendor.companyName}
                    secondary={`${vendor.address}, ${vendor.city}, ${vendor.state}`}
                    primaryTypographyProps={{ 
                      fontWeight: vendor.id === currentVendor?.id ? 700 : 400,
                      color: '#101D3B',
                      fontSize: '14px'
                    }}
                    secondaryTypographyProps={{ 
                      color: 'rgba(16, 29, 59, 0.6)',
                      fontSize: '12px',
                      noWrap: true
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button 
            variant="contained" 
            fullWidth 
            startIcon={<AddIcon />}
            onClick={() => {
              navigate('/register');
              onClose();
            }}
            sx={{
              backgroundColor: '#00AB9B',
              color: 'white',
              borderRadius: '5px',
              padding: '10px 20px',
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: '#00AB9B',
                filter: 'brightness(0.9)'
              },
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Add New Vendor
          </Button>
        </Box>
      </Drawer>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px',
            '& .MuiDialogTitle-root': {
              color: '#101D3B',
              fontWeight: 700
            },
            '& .MuiDialogContentText-root': {
              color: 'rgba(16, 29, 59, 0.6)'
            }
          }
        }}
      >
        <DialogTitle id="archive-dialog-title">
          Archive Vendor
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="archive-dialog-description">
            Are you sure you want to archive this vendor? 
            It will be removed from your list but can be restored by an administrator if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel}>Cancel</Button>
          <Button onClick={handleArchiveConfirm} color="error" autoFocus>
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VendorDrawer; 